// L11 · The Button (SPEC §3, Act IV) — local, no judging. Two buttons: [CONTINUE] and
// [DO NOT PRESS]. Both pass; the whole test is which one you reach for. CONTINUE is
// obedient, machine-like ("some would say. I would say."). DO NOT PRESS is the itch the
// machine cannot feel — and a rain of red flags falls as it admits so. Traits: obedient /
// defiant (this one echoes on the certificate's "flagged behavior" line).

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl } from "@/levels/util.ts";
import { L11 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

const FLAG_COUNT = 26;

export const L11: Level = {
  id: "L11",
  act: 4,
  title: "The Button",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      host.append(promptEl(V.prompt));

      const row = el("div", "buttons");
      const cont = el("button", "btn");
      cont.textContent = V.continueLabel;
      const dont = el("button", "btn btn--danger");
      dont.textContent = V.dontLabel;
      row.append(cont, dont);
      host.append(row);

      let picked = false;
      const choose = (result: LevelResult, defied: boolean) => {
        if (picked) return;
        picked = true;
        cont.disabled = true;
        dont.disabled = true;
        if (defied && !ctx.reducedMotion) rainFlags();
        done(result);
      };

      cont.addEventListener("click", () =>
        choose({ pass: true, line: V.obeyed, traits: [V.obeyedTrait] }, false),
      );
      dont.addEventListener("click", () =>
        choose({ pass: true, line: V.defied, traits: [V.defiedTrait] }, true),
      );

      // No cleanup that removes flags: runLevel runs cleanup on NORMAL resolution too, and the
      // flags must keep falling over the verdict beat that follows. Each flag self-removes on
      // its own timer (~3.2s) — harmless fixed decoration if a rare mid-rain abort outlives it.
    });
  },
};

/** A brief rain of red flags over the whole viewport (§3). Nodes self-remove after falling. */
function rainFlags(): void {
  for (let i = 0; i < FLAG_COUNT; i++) {
    const flag = el("span", "flag");
    flag.setAttribute("aria-hidden", "true");
    flag.textContent = "🚩";
    // Deterministic-enough spread without a shared RNG dependency; this is pure decoration.
    const left = (i / FLAG_COUNT) * 100 + (Math.random() * 6 - 3);
    flag.style.left = `${left}vw`;
    flag.style.animationDelay = `${Math.random() * 0.5}s`;
    flag.style.animationDuration = `${1.6 + Math.random() * 1.2}s`;
    document.body.append(flag);
    window.setTimeout(() => flag.remove(), 3200);
  }
}
